import * as React from "react";
import * as numeral from "numeral";
import { ChangeEvent, Component, MouseEvent } from "react";

import ErrorExplanation from "components/ErrorExplanation";
import SamplePaymentSchedule from "components/SamplePaymentSchedule";
import createWageScheduleRequest from "services/WageScheduleRequest";
import {
  DebtorType,
  EmployerType,
  ScheduleTypeEnum,
  WageScheduleType,
} from "types";
import sampleDatesGenerator from "components/SamplePaymentSchedule/sampleDatesGenerator";
import transformApiSchedule, {
  ClientScheduleType,
} from "utils/transformApiSchedule";
import { getClasses } from "utils";

import EditScheduleFieldset from "./EditScheduleFieldset";
import { ClientErrorsType } from "./types";
import { currencyStringToFloat, schedules } from "./utils";

interface EnhancedWageScheduleType
  extends Partial<ClientScheduleType>,
    WageScheduleType {}

interface PropsType {
  debtor: DebtorType;
  employer: void | EmployerType;
  feeDisplayEnabled: boolean;
  onCancel?(event: MouseEvent): void;
  onScheduleSubmissionSuccess(debtor, employer): void;
  roundingEnabled: boolean;
  setWageSchedule(wageSchedule: WageScheduleType): void;
  wageSchedule: WageScheduleType;
  editMode: boolean;
}

interface StateType {
  clientErrors: ClientErrorsType;
  loading: boolean;
  monthlyPayment: string;
  paycheckAmount: string;
  paycheckMax: number;
  selectedSchedule: string;
  serverErrors: string[];
}

export default class PaymentScheduleForm extends Component<
  PropsType,
  StateType
> {
  static defaultProps = {
    feeDisplayEnabled: true,
    roundingEnabled: false,
  };

  state = {
    clientErrors: {} as ClientErrorsType,
    loading: false,
    monthlyPayment: "0.00",
    paycheckAmount: "0.00",
    paycheckMax: 0.0,
    selectedSchedule: "monthly",
    serverErrors: [],
  };

  wageSchedule: EnhancedWageScheduleType;

  componentDidMount(): void {
    this.wageSchedule = this.buildInitWageSchedule();

    const {
      amountDeductedPerPaycheck,
      amountPerMonth,
      scheduleType,
    } = this.wageSchedule;

    this.calculatePaycheckAmount(amountPerMonth.toString(), scheduleType);

    this.setState(
      {
        monthlyPayment: amountPerMonth.toString(),
        paycheckAmount: amountDeductedPerPaycheck.toString(),
        selectedSchedule: scheduleType,
      },
      this.validate,
    );
  }

  buildInitWageSchedule(): EnhancedWageScheduleType {
    const defaultSchedule = {
      amountDeductedPerPaycheck: 0,
      amountPerMonth: 0,
      scheduleType: "monthly",
    } as WageScheduleType;

    const { debtor, employer, wageSchedule } = this.props;

    if (wageSchedule) return wageSchedule;
    if (!employer) return defaultSchedule;

    if (debtor.wagePull && employer.schedule) {
      const {
        type: scheduleType,
        ...employerWageSchedule
      } = transformApiSchedule(employer.schedule);

      return {
        ...defaultSchedule,
        ...employerWageSchedule,
        scheduleType,
      };
    }

    return defaultSchedule;
  }

  calculatePaycheckAmount = (
    monthlyPaymentString: string,
    schedule: string,
  ): void => {
    const monthlyPayment = currencyStringToFloat(monthlyPaymentString);

    let paycheckAmount = 0;

    switch (schedule) {
      case ScheduleTypeEnum.BI_WEEKLY:
        paycheckAmount = ((monthlyPayment * 12) / 52.0) * 2;
        break;
      case ScheduleTypeEnum.MONTHLY:
      case ScheduleTypeEnum.NTH_WEEKDAY:
      case ScheduleTypeEnum.PARTICULAR_DAY:
        paycheckAmount = monthlyPayment;
        break;
      case ScheduleTypeEnum.TWICE_MONTHLY:
        paycheckAmount = monthlyPayment / 2.0;
        break;
      case ScheduleTypeEnum.WEEKLY:
        paycheckAmount = (monthlyPayment * 12) / 52.0;
        break;
    }

    const threshold = 5.0;
    const paycheckMax = paycheckAmount + threshold;

    this.setState({ paycheckAmount: paycheckAmount.toFixed(2), paycheckMax });
  };

  isFeeDisplayEnabled(): boolean {
    const { employer, feeDisplayEnabled } = this.props;

    return feeDisplayEnabled && (!employer || !employer.corporate);
  }

  isValid = (): boolean => {
    const { clientErrors } = this.state;
    return !Object.keys(clientErrors).length;
  };

  onMonthlyPaymentChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const {
      currentTarget: { value },
    } = e;

    this.setState({ monthlyPayment: value }, (): void => {
      this.validate(() => {
        const { clientErrors, monthlyPayment, selectedSchedule } = this.state;

        if (!clientErrors.monthlyPayment) {
          this.calculatePaycheckAmount(monthlyPayment, selectedSchedule);
        }
      });
    });
  };

  onPaycheckAmountChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const {
      currentTarget: { value: paycheckAmount },
    } = e;

    this.setState({ paycheckAmount }, this.validate);
  };

  onScheduleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const selectedSchedule = e.currentTarget.value;

    this.calculatePaycheckAmount(this.state.monthlyPayment, selectedSchedule);
    this.setState({ selectedSchedule }, this.validate);
  };

  postSchedule = (e): void => {
    e.preventDefault();
    this.setState({ loading: true });

    const { monthlyPayment, paycheckAmount, selectedSchedule } = this.state;

    const {
      debtor: { id: debtorId },
      onScheduleSubmissionSuccess,
      setWageSchedule,
    } = this.props;

    createWageScheduleRequest({
      debtorId,
      monthlyPayment: currencyStringToFloat(monthlyPayment),
      paycheckAmount: currencyStringToFloat(paycheckAmount),
      schedule: selectedSchedule,
    })
      .then((wageSchedule) => {
        setWageSchedule(wageSchedule);
      })
      .then(() => {
        const { editMode, debtor, employer } = this.props;
        if (editMode) {
          window.location.assign(`/employer/employees/${debtor.id}`)
        } else {
          onScheduleSubmissionSuccess(debtor, employer);
        }
      })
      .catch((serverErrors) => {
        this.setState({ loading: false, serverErrors });
      });
  };

  roundUp = (e: MouseEvent): void => {
    e.preventDefault();
    const { clientErrors, paycheckAmount: paycheckAmountString } = this.state;
    const paycheckAmount = currencyStringToFloat(paycheckAmountString);

    const amountToAdd = paycheckAmount % 1 === 0 ? 0.01 : 1.01;
    const adjustedPaycheckAmount = Math.floor(paycheckAmount) + amountToAdd;

    if (!clientErrors.paycheckAmount) {
      this.setState(
        { paycheckAmount: adjustedPaycheckAmount.toString() },
        this.validate,
      );
    }
  };

  validate = (callback?: () => void): ClientErrorsType => {
    let clientErrors: ClientErrorsType = {};

    clientErrors = { ...clientErrors, ...this.validateSchedule() };
    clientErrors = { ...clientErrors, ...this.validateMonthlyPayment() };
    clientErrors = { ...clientErrors, ...this.validatePaycheckAmount() };

    this.setState({ clientErrors }, callback);

    return clientErrors;
  };

  validateMonthlyPayment = (): { monthlyPayment?: string } => {
    const { monthlyPayment: monthlyPaymentString } = this.state;
    const monthlyPayment = currencyStringToFloat(monthlyPaymentString);

    if (isNaN(monthlyPayment)) {
      return { monthlyPayment: "must be a valid number" };
    }

    if (monthlyPayment <= 0) {
      return { monthlyPayment: "must be greater than $0.00" };
    }

    if (monthlyPayment >= 10000.0) {
      const message = `
        You have entered an amount that is over $10,000. While these
        amounts can sometimes happen, they are uncommon. We highly
        recommend that you verify the amount that was entered.
      `;

      return { monthlyPayment: message };
    }

    return {};
  };

  validatePaycheckAmount = (): { paycheckAmount?: string } => {
    const { paycheckAmount: paycheckAmountString, paycheckMax } = this.state;
    const paycheckAmount = currencyStringToFloat(paycheckAmountString);

    if (isNaN(paycheckAmount)) {
      return { paycheckAmount: "must be a valid number" };
    }

    if (paycheckAmount <= 0) {
      return { paycheckAmount: "must be greater than $0.00" };
    }

    if (paycheckAmount >= paycheckMax) {
      const paycheckMaxString = numeral(paycheckMax).format("$0.00");
      const message = `Paycheck Limit (${paycheckMaxString}) Reached`;

      return { paycheckAmount: message };
    }

    return {};
  };

  validateSchedule = (): { schedule?: string } => {
    const { selectedSchedule } = this.state;

    if (!(selectedSchedule in schedules)) {
      return { schedule: "Not a valid schedule type" };
    }

    return {};
  };

  CancelButton = (): JSX.Element => {
    const { onCancel } = this.props;

    if (!onCancel) return null;

    return (
      <button className="button--gray" type="button">
        <i className="fas fa-ban" />
        <span>Cancel</span>
      </button>
    );
  };

  SubmitButton = (): JSX.Element => {
    const { loading } = this.state;
    const disabled = !this.isValid() || loading;
    const submitText = loading ? "Loading..." : "Continue";

    return (
      <button name="commit" type="submit" {...{ disabled }}>
        <span>{submitText}</span>

        <i
          className={getClasses("fas", {
            "fa-long-arrow-alt-right": !loading,
            "fa-spin fa-sync-alt": loading,
          })}
        />
      </button>
    );
  };

  render(): JSX.Element {
    const {
      CancelButton,
      SubmitButton,
      props: { debtor, roundingEnabled },
      state: {
        clientErrors,
        monthlyPayment,
        paycheckAmount,
        paycheckMax,
        selectedSchedule,
        serverErrors,
      },
    } = this;

    return (
      <form id="payment-schedule-form" onSubmit={this.postSchedule}>
        <ErrorExplanation title="Error" errors={serverErrors} />

        <article>
          <EditScheduleFieldset
            errors={clientErrors}
            feeDisplayEnabled={this.isFeeDisplayEnabled()}
            form={this}
            {...{
              debtor,
              monthlyPayment,
              paycheckAmount,
              paycheckMax,
              roundingEnabled,
              selectedSchedule,
            }}
          />

          <section>
            <SamplePaymentSchedule
              paydays={sampleDatesGenerator[selectedSchedule](
                this.wageSchedule,
              )}
              {...schedules[selectedSchedule]}
            />
          </section>
        </article>

        <div className="form--actions">
          <CancelButton />
          <SubmitButton />
        </div>
      </form>
    );
  }
}
